import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { CalendarSingle } from "@/components/CalendarSingle";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ControllerRenderProps } from "react-hook-form";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { toDateTime } from "@/lib/utils/formatters";
import { forwardRef, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DayPickerProps } from "react-day-picker";

interface DatePickerProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  datePickerProps?: Omit<
    DayPickerProps,
    "mode" | "selected" | "onSelect" | "captionLayout"
  >;
  header?: ReactNode;
  footer?: ReactNode;
}

export default forwardRef<HTMLButtonElement, DatePickerProps>(
  function DatePicker(
    {
      className,
      value,
      onChange,
      inDialog = false,
      datePickerProps = {},
      header,
      footer,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const parsedDateTime = toDateTime(value);
    const selectedDate = parsedDateTime?.toJSDate();

    const onChangeInternal = useCallback(
      (date: Date) => {
        onChange(toDateTime(date)?.toFormat(DEFAULT_DATE_FORMAT));
        setOpen(false);
      },
      [onChange],
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-[280px] justify-start text-left font-normal",
              !value && "text-muted-foreground",
              className,
            )}
            {...props}
            ref={ref}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {parsedDateTime ? (
              parsedDateTime.toFormat(DEFAULT_DATE_FORMAT)
            ) : (
              <span>{t("datePicker.pickADate")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-auto p-0 max-h-[min(var(--radix-popover-content-available-height),400px)] overflow-y-auto",
            { "pointer-events-auto": inDialog },
          )}
        >
          {header}
          <CalendarSingle
            {...datePickerProps}
            defaultMonth={selectedDate}
            selected={selectedDate}
            onSelect={onChangeInternal}
          />
          {footer}
        </PopoverContent>
      </Popover>
    );
  },
);
